import * as React from 'react';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import { useNavHeightContext } from 'common/contexts/NavHeightContext';
import { Routes } from 'constants/pages';
import { brandColors } from 'brandColors';
import { PlusSmallIcon } from '@heroicons/react/24/solid';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Permission, useHasPermission } from 'utils/permissions';
import { usePages } from './pages';
import { MenuItem } from './MenuItem';
import { BetaBlock } from '../components/Beta/BetaBlock';
import { DocBlock } from '../components/DocBlock';

export const EXPANDED_DRAWER_WIDTH = 260;

const addPage = {
  title: 'Add Shipments',
  path: Routes.ShipmentsAdd,
  PageIcon: <PlusSmallIcon style={{ width: 18, color: brandColors.colors.white }} />,
  details: {
    color: brandColors.colors.orange.DEFAULT,
  },
};

const openedMixin = (theme: Theme): CSSObject => ({
  width: EXPANDED_DRAWER_WIDTH,
  transition: theme.transitions.create(['width', 'padding-left'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflow: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create(['width', 'padding-left'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflow: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: EXPANDED_DRAWER_WIDTH,
    // flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  })
);

/**
 * The navigation sidebar for the shipper's watchlist
 */
export const Sidebar: React.FC = () => {
  const [open, setOpen] = React.useState(false);

  const pages = usePages();
  const navigate = useNavigate();
  const hasAddPermission = useHasPermission([Permission.ShipmentWatchList]);

  const navHeightContext = useNavHeightContext();

  const [timeoutId, setTimeoutId] = React.useState<NodeJS.Timeout | undefined>(undefined);

  // If the drawer is open, close it after 500ms. If the drawer is closed, open it after 250ms
  const handleMouseHover = (shouldBeOpen: boolean) => {
    // Clear the timeout
    if (timeoutId) clearTimeout(timeoutId);

    const length = shouldBeOpen ? 250 : 500;

    // Set a new timeout to toggle the drawer after one second
    const newTimeoutId = setTimeout(() => {
      // If the drawer is currently open, close it, and vice versa
      if (open !== shouldBeOpen) setOpen(shouldBeOpen);
    }, length);

    // Save the timeout id so we can clear it later
    setTimeoutId(newTimeoutId);
  };

  // Defaults to Active Shipments, i.e. /shipments will show the active shipments page
  return (
    <Drawer
      variant="permanent"
      open={open}
      anchor="left"
      onMouseEnter={() => handleMouseHover(true)}
      onMouseLeave={() => handleMouseHover(false)}
      classes={{ paper: 'z-0 flex flex-col justify-between divide-y' }}
      sx={{
        '& .MuiDrawer-paper': {
          flexDirection: 'none',
        },
      }}
    >
      <List style={{ marginTop: navHeightContext.marginTop + 12, width: EXPANDED_DRAWER_WIDTH }}>
        {hasAddPermission &&
          (!open ? (
            <MenuItem page={addPage} key={addPage.path} open={open} />
          ) : (
            <div
              className="flex flex-col items-center justify-center w-full px-4"
              style={{ minHeight: 64 }}
            >
              <Button
                onClick={() => navigate(addPage.path)}
                variant="contained"
                startIcon={addPage.PageIcon}
                sx={{
                  backgroundColor: addPage.details.color,
                  '&:hover': { backgroundColor: addPage.details.color, opacity: 0.9 },
                }}
                className="w-full"
              >
                {addPage.title}
              </Button>
            </div>
          ))}

        {pages.map((page) => {
          return <MenuItem page={page} key={page.path} open={open} />;
        })}
      </List>
      <div className="divide-y pb-8">
        <DocBlock expanded={open} width={EXPANDED_DRAWER_WIDTH} />
        <BetaBlock expanded={open} width={EXPANDED_DRAWER_WIDTH} />
      </div>
    </Drawer>
  );
};
Sidebar.displayName = 'Shipments.Sidebar';
